import React, { useEffect, useState } from 'react';
import { Grid, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { useFormik, Form, FormikProvider } from 'formik';
import { OrganizationMemberModel } from '../../typings';
import { OrganizationMemberSchema } from '../../validationSchemes';
import { EntityEditDialog, SelectInput, DeleteIcon } from '@lp/lp-ui';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useCurrentUser } from '../../app/useCurrentUser';
import useCache from '../../app/useCache';
import { CachedEntity } from '../../app/cacheSlice';
import { toRoleModels } from '../../functions';
import {
    addUserToOrganization, OrganizationMemberRequest, OrganizationUserRequest, removeUserFromOrganization
} from '../../app/userSlice';
import RoleSelector from '../common/RoleSelector';

type OrganizationMemberDialogProps = {
    open: boolean,
    data: OrganizationMemberModel,
    handleClose: () => void
}

const OrganizationMemberDialog: React.FC<OrganizationMemberDialogProps> = ({ open, data, handleClose }: OrganizationMemberDialogProps) => {
    const dispatch = useAppDispatch();
    const userState = useAppSelector(store => store.user);
    const currentUser = useAppSelector(store => store.currentUser);
    const roles = useCache(CachedEntity.Role);
    const isNew = data.organizationId === 0;
    const [, isTenantAdmin,] = useCurrentUser();
    const [organizationSelected, setOrganizationSelected] = useState<number>(data.organizationId);

    useEffect(() => {
        setOrganizationSelected(data.organizationId);
    }, [data]);

    const formik = useFormik<OrganizationMemberModel>({
        initialValues: data,
        enableReinitialize: true,
        validationSchema: OrganizationMemberSchema,
        onSubmit: async (values) => {
            await handleSubmit(values);
        }
    });

    const {
        errors,
        touched,
        values,
        getFieldProps,
        submitForm
    } = formik;

    const handlePopupClose = () => {
        formik.resetForm();
        handleClose();
    }

    const handleDelete = () => {
        const request: OrganizationUserRequest = {
            userId: userState.id,
            organizationId: organizationSelected
        };
        dispatch(removeUserFromOrganization(request));
        handlePopupClose();
    }

    const handleSubmit = async (organizationMember: OrganizationMemberModel) => {
        const request: OrganizationMemberRequest = {
            userId: userState.id,
            organizationId: organizationMember.organizationId,
            organizationName: organizationMember.organizationName,
            organizationDisplayName: organizationMember.organizationDisplayName,
            roles: organizationMember.roles
        };
        const dispatchResult = await dispatch(addUserToOrganization(request));
        if (dispatchResult.meta.requestStatus === "fulfilled") {
            handlePopupClose();
        }
    };

    const onOrganizationSelected = (event: React.ChangeEvent<any>) => {
        formik.handleChange(event);
        const organizationId = event.target.value;
        setOrganizationSelected(organizationId);
        const organization = currentUser.data?.organizations?.find(o => o.id === organizationId);
        formik.setFieldValue("organizationName", organization ? organization.name : '');
        formik.setFieldValue("organizationDisplayName", organization ? organization.displayName : '');
    }

    return (
        <React.Fragment>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={submitForm}>
                    <EntityEditDialog
                        open={open}
                        editing={isTenantAdmin && (userState?.data?.enabled ?? false)}
                        maxWidth="md"
                        fullWidth
                        title="Organization"
                        handleClose={handlePopupClose}
                        handleSave={submitForm}
                        showMoreMenu={!isNew && isTenantAdmin && (userState?.data?.enabled ?? false)}
                        menuItems={[
                            <MenuItem key="menu-item-i" onClick={handleDelete}>
                                <ListItemIcon>
                                    <DeleteIcon />
                                </ListItemIcon>
                                <ListItemText>
                                    Delete
                                </ListItemText>
                            </MenuItem>
                        ]}
                    >
                        <Grid container spacing={0} columnSpacing={6}>
                            <Grid item xs={12}>
                                <SelectInput
                                    label="Organization"
                                    isEditing={isNew && isTenantAdmin && (userState?.data?.enabled ?? false)}
                                    required
                                    {...getFieldProps("organizationId")}
                                    options={[
                                            { value: 0, label: '-' }, // blank option
                                            ...(currentUser.data?.organizations
                                                ? currentUser.data.organizations.filter(o => !userState.organizations.some(uo => uo.organizationId === o.id) || o.id === organizationSelected)
                                                               .map(o => ({ value: o.id, label: o.displayName }))
                                                : [])
                                        ]}
                                    onChange={onOrganizationSelected}
                                    error={Boolean(touched.organizationId && errors.organizationId)}
                                    helperText={touched.organizationId && errors.organizationId}
                                    value={organizationSelected}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <RoleSelector
                                    roles={roles}
                                    selectedIds={values.roles.map(r => r.id)}
                                    onChange={(ids) => formik.setFieldValue("roles", toRoleModels(ids, roles, values.roles))}
                                    disabled={!isTenantAdmin || !userState?.data?.enabled}
                                    height={380}
                                />
                            </Grid>
                        </Grid>
                    </EntityEditDialog>
                </Form>
            </FormikProvider>
        </React.Fragment>
    );
}

export default OrganizationMemberDialog;
